import type { RuntimeLinearFeature } from "./schema/runtimeTile.ts";
import type { TerrainTileData } from "./schema/tile.ts";
import { sampleTerrainTileData } from "./terrain.ts";
import {
  NERIS_BANK_TERRAIN_MAXIMUM_SPACING_M,
  NERIS_BANK_MINIMUM_HALF_WIDTH_M,
  NERIS_BANK_PATH_CLEARANCE_M,
  NERIS_BANK_PATH_MAXIMUM_DISTANCE_M,
  NERIS_BANK_PATH_MINIMUM_ALIGNMENT,
  NERIS_BANK_PATH_SOURCE_IDS,
  WATER_TERRAIN_BANK_TRANSITION_M,
  WATER_TERRAIN_MAXIMUM_SPACING_M,
} from "../config/water.ts";

const VILNIA_SURFACE_PROFILE = Object.freeze([
  { x: 939, z: -59, y: 10.7 },
  { x: 1_145, z: 674, y: 15.0 },
  { x: 1_018, z: 721, y: 16.0 },
  { x: 953, z: 865, y: 19.5 },
  { x: 1_299, z: 982, y: 20.0 },
  { x: 2_122, z: 924, y: 28.5 },
  { x: 5_114, z: 243, y: 39.5 },
  { x: 8_285, z: -489, y: 60.0 },
  { x: 8_572, z: -476, y: 60.0 },
  { x: 10_109, z: -609, y: 64.2 },
]);

export function normalizedWaterFeatureName(
  feature: Readonly<RuntimeLinearFeature>,
): string {
  return feature.name?.trim().toLocaleLowerCase("lt-LT") ?? "";
}

export function runtimeWaterFeatureWidthM(
  feature: Readonly<RuntimeLinearFeature>,
): number {
  const name = normalizedWaterFeatureName(feature);
  if (name === "neris") {
    return 84;
  }
  if (name === "vilnia") {
    return 14;
  }
  if (feature.classCode === "hc1" || feature.classCode === "fhc1") {
    return 18;
  }
  return feature.classCode.includes("hc3") ? 3.2 : 5.5;
}

export function distanceToRuntimeLinearFeatureM(
  point: Readonly<{ x: number; z: number }>,
  feature: Readonly<RuntimeLinearFeature>,
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < feature.points.length; index += 1) {
    const from = feature.points[index - 1];
    const to = feature.points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    minimum = Math.min(
      minimum,
      pointToLineSegmentDistanceM(point, from, to),
    );
  }
  return minimum;
}

export function pointToLineSegmentDistanceM(
  point: Readonly<{ x: number; z: number }>,
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): number {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
  const alpha = lengthSquared <= Number.EPSILON
    ? 0
    : Math.max(
        0,
        Math.min(
          1,
          ((point.x - from.x) * deltaX +
            (point.z - from.z) * deltaZ) / lengthSquared,
        ),
      );
  return Math.hypot(
    point.x - (from.x + deltaX * alpha),
    point.z - (from.z + deltaZ * alpha),
  );
}

export function pointIntersectsRuntimeWaterSurface(
  point: Readonly<{ x: number; z: number }>,
  feature: Readonly<RuntimeLinearFeature>,
  bankFeatures: readonly Readonly<RuntimeLinearFeature>[] = [],
): boolean {
  const section = nearestRuntimeWaterSection(point, feature);
  if (section === null) {
    return false;
  }
  const widths = runtimeWaterBankWidthsM(feature, section, bankFeatures);
  return section.signedDistanceM >= 0
    ? section.signedDistanceM <= widths.leftM
    : -section.signedDistanceM <= widths.rightM;
}

export interface RuntimeWaterBankSection {
  readonly x: number;
  readonly z: number;
  readonly normalX: number;
  readonly normalZ: number;
}

export interface RuntimeWaterBankWidths {
  readonly leftM: number;
  readonly rightM: number;
}

/**
 * Resolves the authored ribbon width independently on each side of a water
 * centreline. The Neris default remains the fallback, while mapped riverside
 * paths keep the presentation edge from covering real walkable
 * ground. Perpendicular bridge routes are deliberately not bank constraints.
 */
export function runtimeWaterBankWidthsM(
  feature: Readonly<RuntimeLinearFeature>,
  section: Readonly<RuntimeWaterBankSection>,
  bankFeatures: readonly Readonly<RuntimeLinearFeature>[] = [],
): RuntimeWaterBankWidths {
  const defaultHalfWidthM = runtimeWaterFeatureWidthM(feature) / 2;
  if (normalizedWaterFeatureName(feature) !== "neris") {
    return { leftM: defaultHalfWidthM, rightM: defaultHalfWidthM };
  }
  let leftM = defaultHalfWidthM;
  let rightM = defaultHalfWidthM;
  const tangentX = section.normalZ;
  const tangentZ = -section.normalX;
  for (const bank of bankFeatures) {
    if (!isNerisBankPathCandidate(bank)) {
      continue;
    }
    for (let index = 1; index < bank.points.length; index += 1) {
      const from = bank.points[index - 1];
      const to = bank.points[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const deltaX = to.x - from.x;
      const deltaZ = to.z - from.z;
      const lengthM = Math.hypot(deltaX, deltaZ);
      if (lengthM <= 1e-6) {
        continue;
      }
      const alignment = Math.abs(
        deltaX / lengthM * tangentX + deltaZ / lengthM * tangentZ,
      );
      if (alignment < NERIS_BANK_PATH_MINIMUM_ALIGNMENT) {
        continue;
      }
      const closest = closestPointOnLineSegment(section, from, to);
      const offsetX = closest.x - section.x;
      const offsetZ = closest.z - section.z;
      const distanceM = Math.hypot(offsetX, offsetZ);
      if (distanceM > NERIS_BANK_PATH_MAXIMUM_DISTANCE_M) {
        continue;
      }
      const signedDistanceM =
        offsetX * section.normalX + offsetZ * section.normalZ;
      const constrainedHalfWidthM = Math.max(
        NERIS_BANK_MINIMUM_HALF_WIDTH_M,
        Math.abs(signedDistanceM) - NERIS_BANK_PATH_CLEARANCE_M,
      );
      if (signedDistanceM > 0) {
        leftM = Math.min(leftM, constrainedHalfWidthM);
      } else if (signedDistanceM < 0) {
        rightM = Math.min(rightM, constrainedHalfWidthM);
      }
    }
  }
  return { leftM, rightM };
}

/**
 * Returns the stable runtime water level shared by the water mesh and the
 * ground carve beneath it. Neris uses a bounded hydraulic profile because the
 * checked-in coarse terrain contains bridge and embankment elevations that
 * must not lift isolated river sections.
 */
export function runtimeWaterSurfaceElevationM(
  feature: Readonly<RuntimeLinearFeature>,
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<{ x: number; z: number }>,
  localPoint: Readonly<{ x: number; z: number }>,
): number {
  const name = normalizedWaterFeatureName(feature);
  const worldX = tileOrigin.x + localPoint.x;
  const worldZ = tileOrigin.z + localPoint.z;
  if (name === "neris") {
    return Math.max(5, Math.min(11.6, 9.95 + worldX * 0.0008));
  }
  if (name === "vilnia") {
    return nearestProfileElevation(worldX, worldZ, VILNIA_SURFACE_PROFILE);
  }
  return sampleTerrainHeight(terrain, localPoint);
}

/**
 * Builds the one terrain surface used for river rendering, collision and
 * grounded props. River tiles are refined before carving so the water edge no
 * longer has to pull a 16 m terrain vertex far into the bank. That keeps
 * authored bridge landings connected while removing the invisible source
 * terrain that previously let the character and lamp posts float above the
 * rendered riverbank.
 */
export function terrainWithRuntimeWaterCarving(
  tileOrigin: Readonly<{ x: number; z: number }>,
  terrain: Readonly<TerrainTileData>,
  waterFeatures: readonly Readonly<RuntimeLinearFeature>[],
  bankFeatures: readonly Readonly<RuntimeLinearFeature>[] = [],
): Readonly<TerrainTileData> {
  const rivers = waterFeatures
    .map((feature) => ({
      feature,
      name: normalizedWaterFeatureName(feature),
    }))
    .filter(({ name }) => name === "neris" || name === "vilnia");
  if (rivers.length === 0) {
    return terrain;
  }

  const refined = refineTerrainForWater(
    terrain,
    hasMappedNerisBankConstraint(bankFeatures)
      ? NERIS_BANK_TERRAIN_MAXIMUM_SPACING_M
      : WATER_TERRAIN_MAXIMUM_SPACING_M,
  );
  const heightsM = [...refined.heightsM];
  let changed = refined !== terrain;
  for (let index = 0; index < heightsM.length; index += 1) {
    const originalY = heightsM[index];
    if (originalY === undefined) {
      throw new Error(`Terrain height ${index} is missing`);
    }
    const localPoint = {
      x: index % refined.samplesPerSide * refined.spacingM,
      z: Math.floor(index / refined.samplesPerSide) * refined.spacingM,
    };
    let carvedY = originalY;
    for (const { feature, name } of rivers) {
      const section = nearestRuntimeWaterSection(localPoint, feature);
      if (section === null) {
        continue;
      }
      const widths = runtimeWaterBankWidthsM(feature, section, bankFeatures);
      const halfWidthM = section.signedDistanceM >= 0
        ? widths.leftM
        : widths.rightM;
      // Finish the terrain transition at the rendered water edge. The older
      // carve extended a full grid diagonal onto dry ground and then rose in
      // one metre, producing the repeated triangular bank teeth and pulling
      // the mapped riverside path below its own surface.
      const coreRadiusM = Math.max(
        0,
        halfWidthM - WATER_TERRAIN_BANK_TRANSITION_M,
      );
      const bankRadiusM = halfWidthM;
      const distanceM = Math.abs(section.signedDistanceM);
      if (distanceM >= bankRadiusM) {
        continue;
      }
      const waterY = runtimeWaterSurfaceElevationM(
        feature,
        refined,
        tileOrigin,
        localPoint,
      );
      const bedY = waterY - (name === "neris" ? 0.42 : 0.24);
      const bankAlpha = distanceM <= coreRadiusM
        ? 0
        : (distanceM - coreRadiusM) /
          Math.max(bankRadiusM - coreRadiusM, Number.EPSILON);
      const smoothBankAlpha = bankAlpha * bankAlpha * (3 - 2 * bankAlpha);
      carvedY = Math.min(
        carvedY,
        bedY + (originalY - bedY) * smoothBankAlpha,
      );
    }
    if (Math.abs(carvedY - originalY) > 1e-9) {
      heightsM[index] = carvedY;
      changed = true;
    }
  }
  return changed ? { ...refined, heightsM } : terrain;
}

interface NearestRuntimeWaterSection extends RuntimeWaterBankSection {
  readonly signedDistanceM: number;
}

function nearestRuntimeWaterSection(
  point: Readonly<{ x: number; z: number }>,
  feature: Readonly<RuntimeLinearFeature>,
): NearestRuntimeWaterSection | null {
  let nearest: (NearestRuntimeWaterSection & { distanceM: number }) | null =
    null;
  for (let index = 1; index < feature.points.length; index += 1) {
    const from = feature.points[index - 1];
    const to = feature.points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 1e-6) {
      continue;
    }
    const centre = closestPointOnLineSegment(point, from, to);
    const normalX = -deltaZ / lengthM;
    const normalZ = deltaX / lengthM;
    const offsetX = point.x - centre.x;
    const offsetZ = point.z - centre.z;
    const distanceM = Math.hypot(offsetX, offsetZ);
    if (nearest === null || distanceM < nearest.distanceM) {
      nearest = {
        x: centre.x,
        z: centre.z,
        normalX,
        normalZ,
        signedDistanceM: offsetX * normalX + offsetZ * normalZ,
        distanceM,
      };
    }
  }
  return nearest;
}

function closestPointOnLineSegment(
  point: Readonly<{ x: number; z: number }>,
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): Readonly<{ x: number; z: number }> {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
  const alpha = lengthSquared <= Number.EPSILON
    ? 0
    : Math.max(
        0,
        Math.min(
          1,
          ((point.x - from.x) * deltaX +
            (point.z - from.z) * deltaZ) / lengthSquared,
        ),
      );
  return {
    x: from.x + deltaX * alpha,
    z: from.z + deltaZ * alpha,
  };
}

function runtimeSourceFeatureId(
  feature: Readonly<RuntimeLinearFeature>,
): string {
  return feature.id.split("#", 1)[0]?.toLowerCase() ?? "";
}

function hasMappedNerisBankConstraint(
  features: readonly Readonly<RuntimeLinearFeature>[],
): boolean {
  return features.some(isNerisBankPathCandidate);
}

function isNerisBankPathCandidate(
  feature: Readonly<RuntimeLinearFeature>,
): boolean {
  return feature.kind === "path" &&
    feature.sourceId === "osm-footways-2026-08-07" &&
    NERIS_BANK_PATH_SOURCE_IDS.has(runtimeSourceFeatureId(feature));
}

function refineTerrainForWater(
  terrain: Readonly<TerrainTileData>,
  maximumSpacingM: number,
): Readonly<TerrainTileData> {
  const subdivision = Math.max(
    1,
    Math.ceil(
      terrain.spacingM / maximumSpacingM,
    ),
  );
  if (subdivision === 1) {
    return terrain;
  }
  const samplesPerSide = (terrain.samplesPerSide - 1) * subdivision + 1;
  const spacingM = terrain.spacingM / subdivision;
  return {
    samplesPerSide,
    spacingM,
    heightsM: Array.from(
      { length: samplesPerSide * samplesPerSide },
      (_, index) => sampleTerrainHeight(terrain, {
        x: index % samplesPerSide * spacingM,
        z: Math.floor(index / samplesPerSide) * spacingM,
      }),
    ),
  };
}

function sampleTerrainHeight(
  terrain: Readonly<TerrainTileData>,
  localPoint: Readonly<{ x: number; z: number }>,
): number {
  const heightM = sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    localPoint,
  );
  if (heightM === null) {
    throw new Error(
      `Could not sample tile terrain at (${localPoint.x}, ${localPoint.z})`,
    );
  }
  return heightM;
}

function nearestProfileElevation(
  x: number,
  z: number,
  profile: readonly Readonly<{ x: number; z: number; y: number }>[],
): number {
  let nearestElevation = profile[0]?.y ?? 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let index = 1; index < profile.length; index += 1) {
    const from = profile[index - 1];
    const to = profile[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
    const alpha = lengthSquared <= Number.EPSILON
      ? 0
      : Math.max(
        0,
        Math.min(
          1,
          ((x - from.x) * deltaX + (z - from.z) * deltaZ) /
            lengthSquared,
        ),
      );
    const closestX = from.x + deltaX * alpha;
    const closestZ = from.z + deltaZ * alpha;
    const distance = Math.hypot(x - closestX, z - closestZ);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestElevation = from.y + (to.y - from.y) * alpha;
    }
  }
  return nearestElevation;
}
